import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DashboardThemeProvider, ThemeToggleButton } from "@/context/DashboardTheme";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminEstimates from "@/pages/admin/AdminEstimates";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminComplaints from "@/pages/admin/AdminComplaints";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminCenters from "@/pages/admin/AdminCenters";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAutomation from "@/pages/admin/AdminAutomation";
import AdminAgents from "@/pages/admin/AdminAgents";
import AdminAlerts from "@/pages/admin/AdminAlerts";
import AdminCallNotes from "@/pages/admin/AdminCallNotes";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminVisitors from "@/pages/admin/AdminVisitors";
import AdminInsurancePartners from "@/pages/admin/AdminInsurancePartners";
import AdminRewards from "@/pages/admin/AdminRewards";
import AdminOutreach from "@/pages/admin/AdminOutreach";
import AdminCustomerView from "@/pages/admin/AdminCustomerView";
import AdminPrescriptionReview from "@/pages/admin/AdminPrescriptionReview";
import AdminRefillRequests from "@/pages/admin/AdminRefillRequests";
import AdminEmail from "@/pages/admin/AdminEmail";
import { Toaster } from "@/components/ui/sonner";
import { LayoutDashboard, FileText, DollarSign, Package, Users, Building2, Settings, LogOut, Loader2, Bot, Workflow, BellRing, Menu, X, UserCog, Mail, HeartHandshake, Truck, Megaphone, Globe, Flag, PhoneOutgoing, ClipboardCheck, Inbox , Stethoscope } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/estimates", label: "Estimates", icon: FileText },
  { to: "/admin/prescription-review", label: "Prescription Review", icon: ClipboardCheck },
  { to: "/admin/refill-requests", label: "Vet Refill Requests", icon: Stethoscope },
  { to: "/admin/pricing", label: "Pricing Engine", icon: DollarSign },
  { to: "/admin/orders", label: "Orders", icon: Package },
  { to: "/admin/complaints", label: "Complaints", icon: Flag },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/staff", label: "Staff", icon: UserCog },
  { to: "/admin/centers", label: "Fulfillment", icon: Building2 },
  { to: "/admin/insurance-partners", label: "Insurance Partners", icon: HeartHandshake },
  { to: "/admin/rewards", label: "Refill Rewards", icon: Truck },
  { to: "/admin/automation", label: "Automation", icon: Workflow },
  { to: "/admin/agents", label: "AI Agents", icon: Bot },
  { to: "/admin/alerts", label: "Alerts", icon: BellRing },
  { to: "/admin/call-notes", label: "Outbound Calls", icon: PhoneOutgoing },
  { to: "/admin/outreach", label: "Partner Outreach", icon: Megaphone },
  { to: "/admin/email", label: "Email", icon: Inbox },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/visitors", label: "Visitors", icon: Globe },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

function AdminShell() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [hydrated, setHydrated] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => { if (!loading) setHydrated(true); }, [loading]);

  if (!hydrated) {
    return <div className="min-h-screen bg-paw-bg dark:bg-[#0f1117] flex items-center justify-center"><Loader2 className="w-8 h-8 text-paw-text-muted dark:text-white/30 animate-spin" /></div>;
  }
  if (!user || user === false || user.role !== "admin") {
    return <AdminLogin onLogin={() => navigate("/admin", { replace: true })} />;
  }

  const handleLogout = async () => { await logout(); navigate("/admin", { replace: true }); };

  return (
    <div className="min-h-screen bg-paw-bg dark:bg-[#0f1117] text-paw-text dark:text-white flex" data-pawlinx-app="admin" data-testid="admin-layout">
      {/* Mobile topbar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-white dark:bg-[#13161f] border-b border-paw-border dark:border-white/5 flex items-center justify-between px-4">
        <button onClick={() => setOpen(true)} className="p-2 -ml-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5" data-testid="admin-menu-btn"><Menu className="w-5 h-5" /></button>
        <div className="flex items-center gap-2">
          <img src="/logo-light-256.png" alt="PawLinx" className="h-9 w-auto dark:hidden" data-testid="pawlinx-logo" />
          <img src="/logo-dark-256.png" alt="PawLinx" className="h-9 w-auto hidden dark:block" data-testid="pawlinx-logo-dark" />
          <span className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Admin</span>
        </div>
        <ThemeToggleButton />
      </header>

      {/* Mobile drawer overlay */}
      {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-[#13161f] border-r border-paw-border dark:border-white/5 flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="admin-sidebar">
        <div className="px-6 py-6 border-b border-paw-border dark:border-white/5 flex items-center justify-between">
          <div>
            <img src="/logo-light-256.png" alt="PawLinx" className="h-12 w-auto dark:hidden" data-testid="pawlinx-logo" />
            <img src="/logo-dark-256.png" alt="PawLinx" className="h-12 w-auto hidden dark:block" data-testid="pawlinx-logo-dark" />
            <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-1.5">Admin Console</p>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden p-1 rounded hover:bg-black/5 dark:hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg font-manrope text-sm transition-colors ${isActive ? "bg-paw-accent/15 text-paw-accent" : "text-paw-text-secondary dark:text-white/60 hover:text-paw-text dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5"}`} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-paw-border dark:border-white/5 space-y-2">
          <ThemeToggleButton className="w-full justify-start hidden lg:flex" />
          <div className="px-3 py-2">
            <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Signed in as</p>
            <p className="font-manrope text-sm text-paw-text dark:text-white/80 truncate">{user.email}</p>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-manrope text-paw-text-secondary dark:text-white/60 hover:text-paw-text dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 transition-colors" data-testid="admin-logout">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden pt-14 lg:pt-0">
        <Routes>
          <Route path="/" element={<AdminDashboard />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/estimates" element={<AdminEstimates />} />
          <Route path="/admin/prescription-review" element={<AdminPrescriptionReview />} />
          <Route path="/admin/refill-requests" element={<AdminRefillRequests />} />
          <Route path="/admin/pricing" element={<AdminPricing />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/complaints" element={<AdminComplaints />} />
          <Route path="/admin/customers" element={<AdminCustomers />} />
          <Route path="/admin/staff" element={<AdminStaff />} />
          <Route path="/admin/users" element={<Navigate to="/admin/customers" replace />} />
          <Route path="/admin/customers/:userId" element={<AdminCustomerView />} />
          <Route path="/admin/centers" element={<AdminCenters />} />
          <Route path="/admin/insurance-partners" element={<AdminInsurancePartners />} />
          <Route path="/admin/rewards" element={<AdminRewards />} />
          <Route path="/admin/automation" element={<AdminAutomation />} />
          <Route path="/admin/agents" element={<AdminAgents />} />
          <Route path="/admin/alerts" element={<AdminAlerts />} />
          <Route path="/admin/call-notes" element={<AdminCallNotes />} />
          <Route path="/admin/outreach" element={<AdminOutreach />} />
          <Route path="/admin/email" element={<AdminEmail />} />
          <Route path="/admin/newsletter" element={<AdminNewsletter />} />
          <Route path="/admin/visitors" element={<AdminVisitors />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <DashboardThemeProvider storageKey="pawlinx-admin-theme">
      <AuthProvider>
        <AdminShell />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </DashboardThemeProvider>
  );
}
